import { useState } from 'react';
import { Check, Eye, X } from 'lucide-react';
import { t } from '@/i18n';
import { useDocumentsOf, useIssueInvoice, useReviewDocument } from '@/api/hooks/documents';
import type { DocumentSummary, OrderDocSet } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, Field, Textarea } from '@/components/ui/Field';
import { DOC_SLOTS, DocSlotRow } from '@/components/documents/DocSet';
import { toast } from '@/stores/toast';

/**
 * ТРИТЕ ДОКУМЕНТА НА ЗАЯВКАТА — С ДЕЙСТВИЯ, направо от буса. Отвори снимката,
 * приеми я или я върни на шофьора с причина, качи липсващото, издай
 * фактурата — без да се ходи в „Документи" или в самата заявка.
 */
export function OrderDocActions({ orderId, docs, canIssue }: { orderId: string; docs: OrderDocSet; canIssue: boolean }) {
  const files = useDocumentsOf({ orderId });
  const review = useReviewDocument();
  const issue = useIssueInvoice(orderId);
  const [rejecting, setRejecting] = useState<{ document: DocumentSummary; label: string } | null>(null);
  const byId = new Map((files.data?.items ?? []).map((d) => [d.id, d]));

  return (
    <div className="w-full divide-y divide-line rounded-lg border border-line bg-raised/30 px-3">
      {DOC_SLOTS.map((slot) => {
        const value = docs[slot.key];
        const doc = value.documentId ? byId.get(value.documentId) : undefined;
        return (
          <DocSlotRow
            key={slot.key}
            label={slot.label}
            slot={value}
            type={slot.type}
            target={{ orderId }}
            extra={
              <>
                {doc?.previewUrl ? (
                  <a
                    href={doc.previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-accent hover:bg-raised"
                  >
                    <Eye className="size-3.5" />
                    Отвори
                  </a>
                ) : null}
                {value.state === 'pending' && doc ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Check className="size-3.5" />}
                      loading={review.isPending && review.variables?.id === doc.id && review.variables.accept}
                      onClick={() =>
                        review.mutate(
                          { id: doc.id, accept: true },
                          {
                            onSuccess: () => toast.ok(`${slot.label}: приет`),
                            onError: (error) => toast.error('Не е приет', error instanceof Error ? error.message : undefined),
                          },
                        )
                      }
                    >
                      Приеми
                    </Button>
                    <Button size="sm" variant="ghost" icon={<X className="size-3.5" />} onClick={() => setRejecting({ document: doc, label: slot.label })}>
                      Върни
                    </Button>
                  </>
                ) : null}
                {slot.key === 'invoice' && canIssue && value.state === 'missing' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={issue.isPending}
                    onClick={() =>
                      issue.mutate(undefined, {
                        onSuccess: (inv) => toast.ok('Фактурата е издадена', inv.number ?? undefined),
                        onError: (error) => toast.error('Фактурата не е издадена', error instanceof Error ? error.message : undefined),
                      })
                    }
                  >
                    Издай
                  </Button>
                ) : null}
              </>
            }
          />
        );
      })}
      {rejecting ? <RejectDialog document={rejecting.document} label={rejecting.label} onClose={() => setRejecting(null)} /> : null}
    </div>
  );
}

/** Връщане с причина. Ако снимката е от телефона — по избор задача „снимай пак" за шофьора. */
function RejectDialog({ document, label, onClose }: { document: DocumentSummary; label: string; onClose: () => void }) {
  const review = useReviewDocument();
  const [reason, setReason] = useState('');
  const [reshoot, setReshoot] = useState(true);
  const fromDriver = document.driverId !== null;
  const submit = () => {
    if (!reason.trim()) {
      toast.error('Напишете причината', 'Шофьорът я вижда на телефона.');
      return;
    }
    review.mutate(
      { id: document.id, accept: false, reason: reason.trim(), reshoot: reshoot && fromDriver },
      {
        onSuccess: () => {
          toast.ok(`${label}: върнат${reshoot && fromDriver ? ' — шофьорът ще го заснеме наново' : ''}`);
          onClose();
        },
        onError: (error) => toast.error('Не е върнат', error instanceof Error ? error.message : undefined),
      },
    );
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Върни — ${label}`}
      description={document.fileName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="danger" loading={review.isPending} onClick={submit}>
            {t.documents.reject}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t.documents.rejectReason} required>
          <Textarea data-autofocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="напр. не се чете печатът, липсва подписът на получателя" />
        </Field>
        {fromDriver ? (
          <div>
            <Checkbox label={t.documents.reshoot} checked={reshoot} onChange={(e) => setReshoot(e.target.checked)} />
            <p className="mt-1 text-[11px] text-ink-faint">{t.documents.reshootHint}</p>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
